import { useRef, useState, useEffect } from "react";
import { PaymentModel, PaymentStatus, CapturePreAuthRequestDTO, VoidPreAuthRequestDTO, RefundRequestDTO } from "../../../data/entities";
import { sharedProperty } from "../../../hooks";
import { formatDate, toYearMonthDayString } from "../../../utils/dateUtils";
import { formatPrice } from "../../../utils";
import { Button, Icon, IconDefinitions, SlideOutModal, SlideOutModalProps, SpinnerDefault } from "../../../components";
import { CaptureForm, RefundForm, VoidForm } from "./forms";
import InfoSection, { InfoDetail } from "../../../components/InfoSection/InfoSection";
import { PaymentStatusBadge } from "./LocationPaymentGridCells";


export interface PaymentDetailsModalProps extends SlideOutModalProps {
  selectedPaymentState: sharedProperty<PaymentModel>;
  onCapture: (captureRequest: CapturePreAuthRequestDTO) => void;
  onVoid: (voidRequest: VoidPreAuthRequestDTO) => void;
  onRefund: (refundRequest: RefundRequestDTO) => void;
}

export function PaymentDetailsModal(props: Readonly<PaymentDetailsModalProps>) {
  const submitRef = useRef<HTMLButtonElement>(null);
  const [actionType, setActionType] = useState<"capture" | "void" | "refund" | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false); 

  const maxAmount = props.selectedPaymentState.value.total ?? 0;
  const refundedAmount = props.selectedPaymentState.value.refundedAmount ?? 0;
  const remainingAmount = parseFloat((maxAmount - refundedAmount).toFixed(2));

  useEffect(() => {
    setActionType(null);
  }, [props.selectedPaymentState.value.id, props.showState.value]);

  const availableActions: {
    type: "capture" | "void" | "refund";
    label: string;
    icon: IconDefinitions;
  }[] = [];

  if (props.selectedPaymentState.value.status === PaymentStatus.PreAuthorized) {
    availableActions.push({
      type: "capture",
      label: "Capture Pre-Authorization",
      icon: IconDefinitions.moneyBill,
    });
    availableActions.push({
      type: "void",
      label: "Void Pre-Authorization",
      icon: IconDefinitions.trashCan,
    });
  }

  const canRefund =
    (props.selectedPaymentState.value.status === PaymentStatus.Posted ||
      props.selectedPaymentState.value.status === PaymentStatus.Captured) &&
    remainingAmount > 0;

  if (canRefund) {
    availableActions.push({
      type: "refund",
      label: refundedAmount > 0 ? "Refund Remaining Amount" : "Refund Payment",
      icon: IconDefinitions.arrowRotateLeft,
    });
  }

  // Payment details
  const customerDetails: InfoDetail[] = [
    {
      label: "Name",
      value: props.selectedPaymentState.value.invoice?.customer
        ? `${props.selectedPaymentState.value.invoice.customer.firstName} ${props.selectedPaymentState.value.invoice.customer.lastName}`
        : "N/A",
      icon: <Icon iconName={IconDefinitions.user} />,
    },
    {
      label: "Email",
      value: props.selectedPaymentState.value.invoice?.customer
        ? `${props.selectedPaymentState.value.invoice.customer.email}`
        : "N/A",
      icon: <Icon iconName={IconDefinitions.envelope} />,
    },
    {
      label: "Phone",
      value: props.selectedPaymentState.value.invoice?.customer
        ? `${props.selectedPaymentState.value.invoice.customer.phone}`
        : "N/A",
      icon: <Icon iconName={IconDefinitions.phone} />,
    },
  ];

  const transactionDetails: InfoDetail[] = [
    {
      label: "Status",
      value: (
        <PaymentStatusBadge status={props.selectedPaymentState.value.status as PaymentStatus} />
      ),
      icon: <Icon iconName={IconDefinitions.infoCircle} />,
    },
    {
      label: "Provider",
      value: props.selectedPaymentState.value.provider,
      icon: <Icon iconName={IconDefinitions.creditCard} />,
    },
    // Payment information 
    {
      label: props.selectedPaymentState.value.status === PaymentStatus.PreAuthorized 
        ? "Pre-Auth Amount" 
        : "Payment Amount",
      value: formatPrice(maxAmount),
      icon: <Icon iconName={IconDefinitions.moneyBill} />,
      className: "fw-bold",
    },
    {
      label: "Payment Date",
      value: props.selectedPaymentState.value.createdDate
        ? formatDate(toYearMonthDayString(new Date(props.selectedPaymentState.value.createdDate.toLocaleString())))
        : "N/A",
      icon: <Icon iconName={IconDefinitions.calendar} />,
    },
    
    // Refund information 
    ...(refundedAmount > 0 ? [{
      label: "Refunded Amount",
      value: formatPrice(refundedAmount),
      icon: <Icon iconName={IconDefinitions.arrowRotateLeft} />,
      className: "fw-bold text-danger",
    }] : []),
    ...(refundedAmount > 0 && remainingAmount > 0 ? [{
      label: "Remaining Amount",
      value: formatPrice(remainingAmount),
      icon: <Icon iconName={IconDefinitions.moneyBill} />,
      className: "fw-bold text-success",
    }] : []),
    ...(props.selectedPaymentState.value.refundedDate ? [{
      label: "Refund Date",
      value: formatDate(toYearMonthDayString(new Date(props.selectedPaymentState.value.refundedDate.toLocaleString()))),
      icon: <Icon iconName={IconDefinitions.calendar} />,
    }] : []),
  ];

  const handleCapture = async (captureRequest: CapturePreAuthRequestDTO) => {
    setIsSubmitting(true);
    try {
      await props.onCapture(captureRequest);
      props.showState.setter(false);
    } catch (error) {
      console.error('Capture failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVoid = async (voidRequest: VoidPreAuthRequestDTO) => {
    setIsSubmitting(true);
    try {
      await props.onVoid(voidRequest);
      props.showState.setter(false);
    } catch (error) {
      console.error('Void failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRefund = async (refundRequest: RefundRequestDTO) => {
    setIsSubmitting(true);
    try {
      await props.onRefund(refundRequest);
      props.showState.setter(false);
    } catch (error) {
      console.error('Refund failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SlideOutModal
      {...props}
      title="Payment Details"
      // bodyClassName="d-flex flex-column"
      footer={
        <div className="container p-0">
          <div className="row g-0">
            <div className="col-12 pe-2">
              <Button
                onClick={() => props.showState.setter(false)}
                variant="cancel"
                className="w-100"
                disabled={isSubmitting}
              >
                Close
              </Button>
            </div>
            <div className="col-12 ps-2">
              <Button
                onClick={() => submitRef.current?.click()}
                variant="primary"
                className="w-100"
                disabled={isSubmitting}
              >
                Confirm
              </Button>
            </div>
          </div>
        </div>
      }
    >
      <div style={{ position: 'relative', minHeight: '100%' }}>
        {isSubmitting && (
          <SpinnerDefault
            overlay={true}
            message="Processing payment..."
            submessage="Please do not close this window"
            large={true}
          />
        )}
        <InfoSection title="Customer Information" details={customerDetails} />
        <InfoSection title="Transaction Information" details={transactionDetails} />

        {/* Action Cards */}
        {availableActions.length > 0 && (
          <div className="d-flex gap-3 mb-3">
            {availableActions.map((action) => (
              <div
                key={action.type}
                className={`border rounded p-3 flex-grow-1 text-center d-flex flex-column align-items-center justify-content-center`}
                style={{
                  cursor: "pointer",
                  backgroundColor: actionType === action.type ? "var(--bs-primary)" : "#fff",
                  color: actionType === action.type ? "#fff" : "#000",
                  borderColor: actionType === action.type ? "var(--bs-primary)" : "#ddd",
                }}
                onClick={() => setActionType(actionType === action.type ? null : action.type)}
              >
                <Icon iconName={action.icon as string} />
                <div>{action.label}</div>
              </div>
            ))}
          </div>
        )}

        {actionType === "capture" && (
          <CaptureForm
            paymentId={props.selectedPaymentState.value.id ?? 0}
            maxAmount={maxAmount}
            onSubmit={handleCapture}
            submitRef={submitRef}
          />
        )}

        {actionType === "void" && (
          <VoidForm
            paymentId={props.selectedPaymentState.value.id ?? 0}
            onSubmit={handleVoid}
            submitRef={submitRef}
          />
        )}

        {actionType === "refund" && (
          <RefundForm
            paymentId={props.selectedPaymentState.value.id ?? 0}
            remainingAmount={remainingAmount}
            refundedAmount={refundedAmount}
            onSubmit={handleRefund}
            submitRef={submitRef}
          />
        )}
      </div>
    </SlideOutModal>
  );
}

export default PaymentDetailsModal;